// src/components/AdminDashboard.jsx
// ─────────────────────────────────────────────────────────────
// Admin přehled – seznam přihlášek, detail, PDF export
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { fetchAllApplications, getAttachmentUrl, getAdminHeaders, deleteApplication } from '../lib/api';
import { exportApplicationPDF, exportSummaryPDF } from '../lib/pdfExport';
import { QUESTIONS } from '../lib/questions';

// ── Demo data (smazat po napojení Supabase) ─────────────────
const DEMO_APPS = [
  {
    id: 'd1', session_id: 'd1',
    name: 'Tomáš Horák', email: 'tomas.horak@gmail.com', phone: '+420 775 111 222',
    edu: 'Informatika, FIT ČVUT Praha, červen 2023',
    motiv: 'Věřím, že vzdělání je klíč k lepší budoucnosti. Chci vytvořit platformu, která pomůže každému žákovi najít svou cestu.',
    idea: 'AI platforma pro personalizované vzdělávání středoškoláků s adaptivními testy a doporučováním obsahu.',
    area: 'Edtech sektor, žáci 15–19 let, školy i domácí vzdělávání.',
    uniq: 'Na rozdíl od Khan Academy nebo Duolinga nabízíme plně adaptivní curriculum propojené s maturitními požadavky v ČR.',
    demand: 'Trh edtech v ČR je odhadován na 500M Kč ročně. Provedli jsme průzkum s 200 studenty.',
    phase: 'Máme MVP s 50 testovacími uživateli. Připravujeme první komerční spuštění.',
    goals: '1. Získat 500 platících uživatelů\n2. Uzavřít 3 piloty se školami\n3. Dokončit mobilní app',
    risks: 'Technické: škálování AI doporučovacího systému. Byznysové: pomalé rozhodování škol.',
    team: 'Tomáš Horák (CEO, produktový management), Jana Nová (CTO, AI/ML), Petr Blaha (obchod)',
    equip: 'GPU výpočetní kapacity pro trénink modelů.',
    video: 'https://youtu.be/demo1',
    file17_name: 'diplom_horak.pdf',
    status: 'done', pct: 100,
    created_at: new Date(Date.now() - 2 * 24 * 3600 * 1000).toISOString(),
    submitted_at: new Date(Date.now() - 2 * 24 * 3600 * 1000 + 3600 * 1000).toISOString(),
  },
  {
    id: 'd2', session_id: 'd2',
    name: 'Lucie Marková', email: 'lucie.markova@startup.cz', phone: '+420 608 333 444',
    idea: 'Platforma pro cirkulární módu – sdílení a prodej oblečení s AI doporučováním stylu.',
    status: 'partial', pct: 65,
    created_at: new Date(Date.now() - 5 * 3600 * 1000).toISOString(),
    submitted_at: null,
  },
  {
    id: 'd3', session_id: 'd3',
    name: null, email: null,
    status: 'draft', pct: 0,
    created_at: new Date(Date.now() - 1 * 3600 * 1000).toISOString(),
    submitted_at: null,
  },
  {
    id: 'd4', session_id: 'd4',
    name: 'Martin Blaha', email: 'm.blaha@brno.cz', phone: '+420 721 555 666',
    idea: 'SaaS nástroj pro správu komunálního odpadu pomocí IoT senzorů a prediktivní analytiky.',
    video: 'https://youtu.be/demo2', file17_name: 'diplom_blaha.pdf',
    status: 'done', pct: 100,
    created_at: new Date(Date.now() - 4 * 24 * 3600 * 1000).toISOString(),
    submitted_at: new Date(Date.now() - 4 * 24 * 3600 * 1000 + 2 * 3600 * 1000).toISOString(),
  },
];

function StatusBadge({ status }) {
  const cfg = {
    done:    { label: 'Odesláno',     bg: 'rgba(46,125,81,0.1)',  color: '#2E7D51', border: 'rgba(46,125,81,0.2)' },
    partial: { label: 'Rozpracováno', bg: 'rgba(255,193,7,0.1)',  color: '#B8860B', border: 'rgba(255,193,7,0.25)' },
    draft:   { label: 'Otevřeno',     bg: 'rgba(240,90,40,0.1)',  color: '#5b14b3', border: 'rgba(240,90,40,0.2)' },
  }[status] || { label: status, bg: '#eee', color: '#888', border: '#ddd' };

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('cs-CZ') + ' ' + d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

// ── Detail Modal ─────────────────────────────────────────────
function DetailModal({ app, onClose }) {
  if (!app) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16,
          maxWidth: 720, width: '100%', maxHeight: '85vh',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Modal head */}
        <div style={{
          padding: '20px 28px', borderBottom: '1px solid #E8E4DC',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{ fontFamily: 'GT Super Display, serif', fontSize: 22 }}>{app.name || '(bez jména)'}</h3>
            <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
              {app.email} · {fmtDate(app.submitted_at || app.created_at)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => exportApplicationPDF(app)}
              style={{
                background: '#5b14b3', color: '#fff', border: 'none', borderRadius: 6,
                padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              ↓ PDF
            </button>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Modal body */}
        <div style={{ overflowY: 'auto', padding: 28 }}>
          {QUESTIONS.map(q => {
            const fileName = app[`${q.key}_name`];
            const filePath = app[`${q.key}_path`];
            const value = app[q.key];

            let content;
            if (q.type === 'file') {
              content = fileName && filePath
                ? <button
                    onClick={() => downloadAttachment(filePath, fileName)}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#5b14b3', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }}
                  >
                    ↓ {fileName}
                  </button>
                : <span style={{ color: '#bbb', fontStyle: 'italic' }}>Nenahrán</span>;
            } else if (q.type === 'url') {
              content = value
                ? <a href={value} target="_blank" rel="noreferrer" style={{ color: '#5b14b3' }}>{value}</a>
                : <span style={{ color: '#bbb', fontStyle: 'italic' }}>Nevyplněno</span>;
            } else {
              content = value
                ? <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>
                : <span style={{ color: '#bbb', fontStyle: 'italic' }}>Nevyplněno</span>;
            }

            return (
              <div key={q.key} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #E8E4DC' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#5b14b3', marginBottom: 5 }}>
                  {q.qNum} — {q.label}
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.7, color: '#1A1A1A' }}>
                  {content}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main AdminDashboard ──────────────────────────────────────
export default function AdminDashboard() {
  const [apps,      setApps]      = useState([]);
  const [search,    setSearch]    = useState('');
  const [selected,  setSelected]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [fetchError, setFetchError] = useState(false);

  // Stáhne přílohu přes fetch s auth headerem (heslo se nedostane do URL)
  const downloadAttachment = useCallback(async (filePath, fileName) => {
    try {
      const res = await fetch(getAttachmentUrl(filePath), { headers: getAdminHeaders() });
      if (!res.ok) return;
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  }, []);

  async function handleDelete(app, e) {
    e.stopPropagation();
    if (!window.confirm(`Opravdu smazat přihlášku od „${app.name || '(bez jména)'}"?`)) return;
    const ok = await deleteApplication(app.id);
    if (ok) setApps(prev => prev.filter(a => a.id !== app.id));
  }

  useEffect(() => {
    setLoading(true);
    fetchAllApplications().then(data => {
      if (data === null) {
        setFetchError(true);
      } else {
        setApps(data);
      }
      setLoading(false);
    });
  }, []);

  const filtered = apps.filter(a => {
    const q = search.toLowerCase();
    return (
      (a.name  || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q) ||
      (a.idea  || '').toLowerCase().includes(q)
    );
  });

  const stats = {
    total:   apps.length,
    done:    apps.filter(a => a.status === 'done').length,
    partial: apps.filter(a => a.status === 'partial').length,
    draft:   apps.filter(a => a.status === 'draft').length,
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 80px' }}>

      {/* Hero */}
      <div style={{
        background: '#5b14b3', color: '#fff', borderRadius: 12,
        padding: 32, marginBottom: 32,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 20, flexWrap: 'wrap',
      }}>
        <div>
          <h2 style={{ fontFamily: 'GT Super Display, serif', fontSize: 26 }}>Přehled přihlášek</h2>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
            Founder Stipendium 2026 · Aktualizováno: {new Date().toLocaleString('cs-CZ')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Stats */}
          {[
            { num: stats.total,   lbl: 'Celkem' },
            { num: stats.done,    lbl: 'Odesláno' },
            { num: stats.partial, lbl: 'Rozepsáno' },
            { num: stats.draft,   lbl: 'Jen otevřeno' },
          ].map(s => (
            <div key={s.lbl} style={{
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10, padding: '12px 20px', textAlign: 'center',
            }}>
              <div style={{ fontFamily: 'GT Super Display, serif', fontSize: 28, color: '#fed875', lineHeight: 1 }}>{s.num}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>{s.lbl}</div>
            </div>
          ))}
          {/* Export button */}
          <button
            onClick={() => exportSummaryPDF(apps.filter(a => a.status === 'done'))}
            style={{
              background: '#fed875', color: '#5b14b3', border: 'none', borderRadius: 8,
              padding: '12px 20px', fontFamily: 'GT America, sans-serif', fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            ↓ Export PDF
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #E8E4DC', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #E8E4DC',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <h3 style={{ fontWeight: 600, fontSize: 16 }}>Všechny přihlášky</h3>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Hledat jméno, e-mail, nápad…"
            style={{
              background: '#F5F3EF', border: '1px solid #E8E4DC', borderRadius: 8,
              padding: '8px 14px', fontFamily: 'GT America, sans-serif', fontSize: 14, color: '#1A1A1A',
              outline: 'none', width: 260,
            }}
          />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 40 }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: 130 }} />
            </colgroup>
            <thead>
              <tr>
                {['#', 'Jméno', 'E-mail', 'Startup nápad', 'Stav', 'Vyplněno %', 'Datum', ''].map(th => (
                  <th key={th} style={{
                    background: '#F5F3EF', padding: '10px 14px', textAlign: 'left',
                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.07em', color: '#888', borderBottom: '1px solid #E8E4DC',
                  }}>{th}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#888' }}>Načítám…</td></tr>
              )}
              {!loading && fetchError && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#c0392b' }}>
                  Nepodařilo se načíst přihlášky. Zkontrolujte připojení k serveru nebo se přihlašte znovu.
                </td></tr>
              )}
              {!loading && !fetchError && filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#888' }}>Žádné přihlášky</td></tr>
              )}
              {filtered.map((app, i) => (
                <tr key={app.id} style={{ borderBottom: '1px solid #E8E4DC' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FAF8F5'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '13px 14px', fontSize: 12, color: '#888' }}>{i + 1}</td>
                  <td style={{ padding: '13px 14px', fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {app.name || <span style={{ color: '#bbb', fontStyle: 'italic' }}>(bez jména)</span>}
                  </td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.email || '—'}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {app.idea ? app.idea.slice(0, 60) + (app.idea.length > 60 ? '…' : '') : '—'}
                  </td>
                  <td style={{ padding: '13px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <StatusBadge status={app.status} />
                      {app.status === 'done' && !app.admin_email_sent_at && (
                        <span title="Email adminovi nebyl odeslan" style={{ fontSize: 14, cursor: 'default' }}>⚠️</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '13px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 60, height: 5, background: '#E8E4DC', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${app.pct || 0}%`, height: '100%', background: '#5b14b3', borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 12, color: '#888' }}>{app.pct || 0}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '13px 14px', fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>{fmtDate(app.created_at)}</td>
                  <td style={{ padding: '13px 14px' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button
                        onClick={() => setSelected(app)}
                        style={{
                          background: 'none', border: '1px solid #E8E4DC', borderRadius: 6,
                          padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                          color: '#1A1A1A', transition: 'all 0.15s', fontFamily: 'GT America, sans-serif', whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => { e.target.style.borderColor = '#5b14b3'; e.target.style.color = '#5b14b3'; }}
                        onMouseLeave={e => { e.target.style.borderColor = '#E8E4DC'; e.target.style.color = '#1A1A1A'; }}
                      >
                        Detail →
                      </button>
                      <button
                        onClick={e => handleDelete(app, e)}
                        style={{
                          background: 'none', border: '1px solid #E8E4DC', borderRadius: 6,
                          padding: '5px 7px', cursor: 'pointer', color: '#c0392b',
                          transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#c0392b'; e.currentTarget.style.background = 'rgba(192,57,43,0.06)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#E8E4DC'; e.currentTarget.style.background = 'none'; }}
                        title="Smazat přihlášku"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <DetailModal app={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
